/**
 * «Бүх гутал»-ын төлөв — БҮГД URL-д. Холбоос хуваалцаж, дахин ачаалж
 * болно; header-ийн хайлт `?q=`, агуулахын хуудас `?storeId=&status=`
 * гэж энд ирнэ.
 *
 * Анхдагч утга URL-д бичигдэхгүй — шүүлтүүргүй хуудас нь цэвэр `/items`.
 */

import type { ItemOrigin, ItemSort, ItemStatus } from '@boots/schema';
import { useSearchParams } from 'react-router-dom';
import { STATUS_LABEL } from './types';

/** '' = «Нөөцөд» (анхдагч) · 'all' = бүх төлөв · тодорхой нэг төлөв */
export type StatusScope = '' | 'all' | ItemStatus;

export interface ItemsFilter {
  q: string;
  status: StatusScope;
  storeId: string;
  locationId: string;
  supplierId: string;
  /** ₮, зөвхөн цифр. '' = хязгааргүй */
  priceMin: string;
  priceMax: string;
  unlabeled: boolean;
  unpriced: boolean;
  /** '' = аль аль нь · аялал · «Эхний үлдэгдэл» */
  origin: '' | ItemOrigin;
  /** '' = сонгоогүй → зөвхөн размерын карт · 'all' · 'none' (размергүй) · '38' */
  size: string;
  sort: ItemSort;
  view: 'grid' | 'table';
}

type UrlKey = keyof ItemsFilter;

export type SetItemsFilter = (kv: Partial<Record<UrlKey, string | null>>, opts?: { push?: boolean }) => void;

const SORTS: readonly ItemSort[] = ['newest', 'oldest', 'price_asc', 'price_desc'];
const STATUSES = Object.keys(STATUS_LABEL);

/** Сервер 20–60-ыг л хүлээж авна (`ItemFilterQuery.size`). */
const isSize = (v: string) => /^\d{2}$/.test(v) && Number(v) >= 20 && Number(v) <= 60;

/** Хүчингүй `?size=` хаягдана — 400 харуулахын оронд размерын нүүр. */
function readSize(v: string | null): string {
  if (v === 'all' || v === 'none') return v;
  return v && isSize(v) ? v : '';
}

/**
 * Хайлтын талбарт «38» + Enter → 38 размер (хуудас ба header хоёулаа).
 * Хоёр оронтой хэсэгчилсэн кодоор хайх нь утгагүй — бараг бүх гутал таарна.
 */
export function sizeFromSearch(text: string): string | null {
  const v = text.trim();
  return isSize(v) ? v : null;
}

const readPrice = (v: string | null) => (v && /^\d{1,9}$/.test(v) ? v : '');

export function useItemsFilter() {
  const [params, setParams] = useSearchParams();

  const status = params.get('status') ?? '';
  const sort = params.get('sort') as ItemSort | null;
  const origin = params.get('origin');
  const filter: ItemsFilter = {
    q: params.get('q') ?? '',
    status: status === 'all' || STATUSES.includes(status) ? (status as StatusScope) : '',
    storeId: params.get('storeId') ?? '',
    locationId: params.get('locationId') ?? '',
    supplierId: params.get('supplierId') ?? '',
    priceMin: readPrice(params.get('priceMin')),
    priceMax: readPrice(params.get('priceMax')),
    unlabeled: params.get('unlabeled') === 'true',
    unpriced: params.get('unpriced') === 'true',
    origin: origin === 'trip' || origin === 'opening' ? origin : '',
    size: readSize(params.get('size')),
    sort: sort && SORTS.includes(sort) ? sort : 'newest',
    view: params.get('view') === 'table' ? 'table' : 'grid',
  };

  /**
   * Размер солих нь ШИНЭ түүх — браузерын «Буцах» размерын карт руу
   * буцаана. Шүүлтүүр одоогийн мөрийг дарж бичнэ, түүх дүүргэхгүй.
   */
  const set: SetItemsFilter = (kv, opts) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(kv)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: !opts?.push });
  };

  return { filter, set };
}

/** «Цэвэрлэх» — хайлт, размер, эрэмбэ, харагдацыг ХӨНДӨХГҮЙ. */
export const CLEAR_FILTERS: Partial<Record<UrlKey, null>> = {
  status: null, storeId: null, locationId: null, supplierId: null,
  priceMin: null, priceMax: null, unlabeled: null, unpriced: null, origin: null,
};

export function activeFilterCount(f: ItemsFilter): number {
  return [
    f.status, f.storeId, f.locationId, f.supplierId, f.priceMin || f.priceMax, f.unlabeled, f.unpriced, f.origin,
  ].filter(Boolean).length;
}

/**
 * `GET /items` ба `/items/facets`-ийн шүүлтүүр — хоёулаа ИЖИЛ, эс
 * тэгвээс карт дээрх тоо жагсаалттай зөрнө. Эрэмбэ, хуудаслалт энд
 * орохгүй.
 */
export function itemsApiFilter(f: ItemsFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.status === '') p.set('inStock', 'true');
  else if (f.status !== 'all') p.set('status', f.status);
  if (f.storeId) p.set('storeId', f.storeId);
  if (f.locationId) p.set('locationId', f.locationId);
  if (f.supplierId) p.set('supplierId', f.supplierId);
  if (f.priceMin) p.set('priceMin', f.priceMin);
  if (f.priceMax) p.set('priceMax', f.priceMax);
  if (f.unlabeled) p.set('unlabeled', 'true');
  if (f.unpriced) p.set('unpriced', 'true');
  if (f.origin) p.set('origin', f.origin);
  if (f.size === 'none') p.set('unsized', 'true');
  else if (f.size && f.size !== 'all') p.set('size', f.size);
  return p;
}
